import { useQuery } from '@tanstack/react-query'
import { useWistorAuth } from '@/api/auth'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui-primitives/dropdown-menu'
import { TopBarButton } from '@/components/editor/configuration-top-bar/primitives'
import { ChevronDown, Loader2Icon, LogOut, UserRound } from 'lucide-react'
import { switchConfiguratorUx } from '@/components/editor/experimental-ux'
import { Switch } from '@/components/ui-primitives/switch'
import { FlaskConical } from "lucide-react"

export function UserMenu() {
  const auth = useWistorAuth()
  const userName = auth.status === "authorized" ? auth.user.name : "Anonymous"
  const email = auth.status === "authorized" ? auth.user.email : null
  const emailHash = useQuery<string>({
    enabled: Boolean(email),
    queryKey: [ "email-hash", email ],
    queryFn: () => sha256Hex(email ?? "")
  })

  const isLoading = !emailHash.data || auth.status === "authenticating"

  return (
    <DropdownMenu>
      <DropdownMenuTrigger aria-label={`User menu (${userName})`}>
        <TopBarButton
          title={email ? `User (${email})` : `User`}
          className="flex gap-1.5 items-center rounded focus:outline-none">
          { isLoading ? (
            <Loader2Icon
              size={18}
              className="animate-spin opacity-50"
            />
          ) : (
            auth.status === "authorized" ? (
              <UserRound size={22} />
              // <img alt="user avatar"
              //      className="w-[22px] h-[22px] rounded-full outline outline-1 outline-black"
              //      src={`https://seccdn.libravatar.org/avatar/${emailHash.data}?s=200&d=identicon&s=64`} />
            ) : (
              <div className="
                  w-[22px] h-[22px] flex items-center justify-center
                  rounded-full outline outline-1 outline-black text-gray-200
                  bg-indigo-700 bg-opacity-25">?</div>
            )
          )}
          <ChevronDown size={14} className="opacity-75" />
        </TopBarButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent style={{ zIndex: 2100 /* happy-todo: revisit z-index */ }}
                           className="p-2 bg-[#110b37] text-white font-exo select-none text-xs !border-0 shadow-md">
        <div className="flex items-center gap-1 mx-2 pt-[2px] pb-2 mb-1 border-b-2 border-gray-200 border-opacity-25">
          <div className="flex-shrink-0">
            <UserRound size={22} />
            {/*<img className="w-8 h-8 rounded-full outline outline-1 outline-black"*/}
            {/*     src={`https://seccdn.libravatar.org/avatar/${emailHash.data}?s=200&d=identicon&s=64`} />*/}
          </div>
          <div className="flex-grow flex flex-col gap-0.5 px-2 ">
            <div className="opacity-80 font-medium">{userName}</div>
            <div className="opacity-60">{email}</div>
          </div>
        </div>
        <div className=" ">
          <DropdownMenuItem className="flex gap-2 text-xs cursor-pointer py-[3px]"
                            onClick={() => switchConfiguratorUx(false)}>
            <svg width="0" height="0" className="absolute" aria-hidden>
              <defs>
                <linearGradient id="experimental-ux-gradient" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%" stopColor="#c4b5fd" />
                  <stop offset="55%" stopColor="#818cf8" />
                  <stop offset="100%" stopColor="#67e8f9" />
                </linearGradient>
              </defs>
            </svg>
            <FlaskConical size={16} stroke="url(#experimental-ux-gradient)" />
            <span className="grow">New configurator enabled</span>
            <Switch checked className="
                  m-0 cursor-pointer scale-75 data-[state=checked]:bg-indigo-400
                  opacity-90 hover:opacity-100" />
          </DropdownMenuItem>
          <DropdownMenuItem className="flex gap-2 text-xs cursor-pointer">
            <div className="min-h-4 min-w-4" />
            Log out / reset app session
          </DropdownMenuItem>
          <DropdownMenuItem className="flex gap-2 text-xs cursor-pointer">
            <div className="min-h-4 min-w-4" />
            Log in as different user
          </DropdownMenuItem>
          <DropdownMenuItem className="flex gap-2 text-xs cursor-pointer">
            <LogOut size={16} className="h-4 w-4" /> Log out platform
          </DropdownMenuItem>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

async function sha256Hex(input: string) {
  const data = new TextEncoder().encode(input.trim().toLowerCase())
  const hash = await crypto.subtle.digest('SHA-256', data)
  return [...new Uint8Array(hash)]
    .map(b => b.toString(16).padStart(2, '0'))
    .join('')
}
